// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Volume2, VolumeX } from 'lucide-react';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import useAutoSpeakPreference from '../hooks/useAutoSpeakPreference';
import SpeakButton from './SpeakButton';
import MicButton from './MicButton';
import SpectralBot from './SpectralBot';
import { STANDARD_BOT_URL, BOT_SPECTRA } from '../data/federationAssets';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// SFUI-004 R3: Tour/Operator bots use the BLUE spectral variant.
const TOUR_BOT_AVATAR = STANDARD_BOT_URL;
const TOUR_BOT_SPECTRUM = 'blue';
const TOUR_BOT_FILTER = BOT_SPECTRA[TOUR_BOT_SPECTRUM].filter;

/**
 * Federation Tour Guide — floating persistent Operator persona.
 *
 * Architect directive (2026-05-23): a global tour-bot that lives on
 * every page of Xavier, opens a voice-enabled chat popup, and acts
 * as the Federation's friendly guide ("Tour guide · system assistant
 * · security").
 *
 * Conversations route through `/api/chat/` with a tour_persona flag so
 * the backend can apply an Operator system prompt (graceful no-op if
 * the backend doesn't yet branch on it).
 */
export default function FederationTourBot() {
  const { supported: ttsSupported, speaking, speak, stop } = useSpeechSynthesis();
  const stt = useSpeechRecognition({ continuous: false, interim: true });
  const { autoSpeakEnabled, toggleAutoSpeak } = useAutoSpeakPreference();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "Welcome to the NextXus Federation. I'm the Tour Guide — I can show you Xavier's courses, the 112 Sacred Directives (Code Book Vol 1), and how to reach our sibling nodes. Ask me anything or tap the mic to speak.",
      timestamp: new Date(),
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(
    () => `tour_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  );
  const messagesEndRef = useRef(null);

  // Sync STT transcript into input box live
  useEffect(() => {
    if (stt.transcript) setInput(stt.transcript);
  }, [stt.transcript]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-open once per visitor (~10s after page load, so the UniversalTour can finish first)
  useEffect(() => {
    try {
      const seen = localStorage.getItem('xavier.tour_bot.autoopened.v1');
      if (seen) return undefined;
      const t = setTimeout(() => {
        setOpen(true);
        try { localStorage.setItem('xavier.tour_bot.autoopened.v1', new Date().toISOString()); } catch { /* ignore */ }
      }, 12000);
      return () => clearTimeout(t);
    } catch {
      return undefined;
    }
  }, []);

  // Auto-speak the latest bot message (Voice Mandate)
  useEffect(() => {
    if (!autoSpeakEnabled || !ttsSupported || !open) return;
    const last = messages[messages.length - 1];
    if (!last || last.type !== 'bot') return;
    speak(last.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoSpeakEnabled, ttsSupported, open]);

  const send = async () => {
    if (!input.trim() || loading) return;
    if (stt.listening) stt.stop();

    const userText = input.trim();
    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, type: 'user', text: userText, timestamp: new Date() },
    ]);
    setInput('');
    stt.reset();
    setLoading(true);

    try {
      const resp = await fetch(`${API_URL}/api/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          session_id: sessionId,
          persona: 'operator_tour_guide',
        }),
      });
      const data = await resp.json();
      const text =
        (resp.ok && data?.response) ||
        "I'm having trouble reaching the Federation core. Try again in a moment.";
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, type: 'bot', text, timestamp: new Date() },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          type: 'bot',
          text: "Connection error. The Federation lattice may be syncing.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher — bottom-left so it doesn't fight with Chatbot bottom-right */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="tour-bot-trigger"
        aria-label="Open Federation Tour Guide"
        title="Federation Tour Guide · Operator persona"
        style={{
          position: 'fixed',
          left: '24px',
          bottom: 'calc(56px + 24px)',
          zIndex: 95,
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          border: '2px solid #22d3ee',
          background: '#0a0a0a',
          boxShadow: '0 0 18px rgba(34, 211, 238, 0.45), inset 0 0 0 1px rgba(255,255,255,0.08)',
          cursor: 'pointer',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        {open ? (
          <X style={{ width: '24px', height: '24px', color: '#22d3ee', margin: 'auto' }} />
        ) : (
          <img
            src={TOUR_BOT_AVATAR}
            alt="Federation Tour Guide bot"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: TOUR_BOT_FILTER,
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        {/* Live status dot */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: '6px',
            right: '6px',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#22d3ee',
            boxShadow: '0 0 8px #22d3ee',
            border: '2px solid #0a0a0a',
          }}
        />
      </button>

      {/* Chat popup */}
      {open && (
        <div
          data-testid="tour-bot-popup"
          style={{
            position: 'fixed',
            left: '24px',
            bottom: 'calc(56px + 104px)',
            zIndex: 95,
            width: '380px',
            maxWidth: 'calc(100vw - 48px)',
            height: '520px',
            background: 'linear-gradient(180deg, #0a0a0a 0%, #050b14 100%)',
            border: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '16px',
            boxShadow: '0 0 40px rgba(0,0,0,0.6), 0 0 30px rgba(34, 211, 238, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            color: '#e5e7eb',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid #cffafe',
                flexShrink: 0,
              }}
            >
              <img
                src={TOUR_BOT_AVATAR}
                alt="Federation Tour Guide bot"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: TOUR_BOT_FILTER,
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#cffafe' }}>
                Operator · Federation Tour Guide
              </div>
              <div style={{ fontSize: '11px', color: '#a5f3fc', opacity: 0.9 }}>
                Tour guide · system assistant · security
              </div>
            </div>
            <button
              onClick={() => { if (autoSpeakEnabled) stop(); toggleAutoSpeak(); }}
              data-testid="tour-bot-autospeak"
              aria-label={autoSpeakEnabled ? 'Mute auto voice' : 'Enable auto voice'}
              title={autoSpeakEnabled ? 'Auto-voice ON — click to mute' : 'Auto-voice OFF — click to enable'}
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: '#cffafe',
                border: 'none',
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '6px',
              }}
            >
              {autoSpeakEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" style={{ opacity: 0.7 }} />}
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="tour-bot-close"
              aria-label="Close tour guide"
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: '#cffafe',
                border: 'none',
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: m.type === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '10px 14px',
                    borderRadius: '14px',
                    background: m.type === 'user' ? '#0891b2' : 'rgba(255,255,255,0.06)',
                    color: '#e5e7eb',
                    fontSize: '13px',
                    lineHeight: 1.5,
                  }}
                >
                  <p style={{ margin: 0 }}>{m.text}</p>
                  {m.type === 'bot' && (
                    <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'flex-end' }}>
                      <SpeakButton
                        text={m.text}
                        supported={ttsSupported}
                        speaking={speaking}
                        onSpeak={speak}
                        onStop={stop}
                        testId={`speak-tour-msg-${m.id}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '10px 14px', borderRadius: '14px', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#22d3ee' }} />
                  <span style={{ fontSize: '12px' }}>Operator is checking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div style={{ padding: '12px', borderTop: '1px solid rgba(34, 211, 238, 0.2)', display: 'flex', gap: '8px', alignItems: 'center', background: 'rgba(0,0,0,0.4)' }}>
            <MicButton
              supported={stt.supported}
              listening={stt.listening}
              onStart={stt.start}
              onStop={stt.stop}
              testId="tour-bot-mic"
              label="Speak your question"
            />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={onKey}
              placeholder={stt.listening ? 'Listening…' : 'Type or speak…'}
              disabled={loading}
              data-testid="tour-bot-input"
              style={{
                flex: 1,
                padding: '10px 14px',
                background: '#000',
                border: '1px solid rgba(34, 211, 238, 0.25)',
                borderRadius: '10px',
                color: '#e5e7eb',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="tour-bot-send"
              aria-label="Send"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                border: 'none',
                background: input.trim() && !loading ? '#fbbf24' : 'rgba(255,255,255,0.1)',
                color: input.trim() && !loading ? '#000' : '#9ca3af',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'background 160ms ease',
              }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
